import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { IMAGES, FALLBACK_PRODUCTS } from "@/data/site";
import { scrollToId } from "@/lib/scroll";
import { LeafMark } from "@/components/LeafMark";
import { MaskLine, EASE } from "@/components/motion";

const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 200]);
  const fade = useTransform(scrollY, [0, 600], [1, 0.25]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 16 });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onTiltEnd = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="home"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, opacity: fade }}>
        <img
          src={IMAGES.hero}
          alt="Tea being poured in warm amber light"
          className="h-[115%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/80 to-stone-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-transparent to-stone-950/60" />
      </motion.div>

      <motion.div
        className="pointer-events-none absolute -left-10 top-24 hidden text-amber-600/25 lg:block"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 1.2, ease: EASE }}
      >
        <div className="animate-drift">
          <LeafMark className="h-40 w-40" strokeWidth={1} />
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-5 pb-28 pt-32 sm:px-8 lg:grid-cols-[1.2fr_0.8fr] lg:pb-20">
        <div>
          <motion.p
            data-testid="hero-overline"
            className="mb-8 flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.4em] text-amber-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
          >
            <span className="inline-block h-px w-10 bg-amber-600" />
            Small-batch · Single-estate · Blended in India
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-serif text-[clamp(3.2rem,8.5vw,7.25rem)] leading-[0.95] tracking-tight text-stone-100"
          >
            <MaskLine delay={0.35}>Steeped in</MaskLine>
            <MaskLine delay={0.5}>
              <em className="not-italic text-amber-500">amber</em> light,
            </MaskLine>
            <MaskLine delay={0.65}>poured slow.</MaskLine>
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            className="mt-8 max-w-md text-base font-light leading-relaxed text-stone-400 md:text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9, ease: EASE }}
          >
            Twelve small-batch teas from the gardens of Darjeeling and Assam — fired, rested
            and sealed by hand. Amber Brew Tea is a house built on patience.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9, ease: EASE }}
          >
            <button
              data-testid="hero-cta-collection"
              onClick={() => scrollToId("#collection")}
              className="group inline-flex items-center gap-3 rounded-full bg-amber-600 px-7 py-3.5 text-sm font-medium text-stone-950 transition-all duration-300 hover:bg-amber-500 hover:shadow-[0_0_36px_rgba(217,119,6,0.35)]"
            >
              Explore the Collection
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-story"
              onClick={() => scrollToId("#story")}
              className="inline-flex items-center gap-3 rounded-full border border-white/15 px-7 py-3.5 text-sm font-light text-stone-300 transition-colors duration-300 hover:border-amber-600/60 hover:text-amber-500"
            >
              Our Story
            </button>
          </motion.div>
        </div>

        <motion.div
          data-testid="hero-feature-card"
          className="relative hidden justify-end lg:flex"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 1.2, ease: EASE }}
          style={{ perspective: 900 }}
        >
          <motion.div
            onMouseMove={onTilt}
            onMouseLeave={onTiltEnd}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="group relative w-[340px] overflow-hidden border border-white/10 bg-stone-900 shadow-[0_40px_80px_rgba(0,0,0,0.6)]"
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={FALLBACK_PRODUCTS[0].image}
                alt="A glass of Darjeeling tea glowing amber"
                className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-transparent to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6" style={{ transform: "translateZ(40px)" }}>
              <p className="text-[10px] uppercase tracking-[0.35em] text-amber-500">Signature pour</p>
              <p className="mt-2 font-serif text-2xl tracking-tight text-stone-100">
                Darjeeling First Flush
              </p>
              <p className="mt-1 text-xs font-light text-stone-400">Muscatel · Floral · Bright</p>
            </div>
            <div className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 spotlight" />
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("#collection")}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3 text-stone-500 transition-colors duration-300 hover:text-amber-500"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        aria-label="Scroll to collection"
      >
        <span className="text-[10px] uppercase tracking-[0.4em]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown className="h-4 w-4" />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
